import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import clsx from 'clsx';
import { Check, ChevronDown } from 'lucide-react';
import type { ComponentProps } from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TSelectBase } from 'types/general.types';
import { HubertButton } from '../HubertButton';
import HubertPopover from '../HubertPopover';
import TruncatedLabel from './TruncatedLabel';
import { toggleSelection } from './toggleSelection';

interface Props extends ComponentProps<typeof HubertPopover> {
  itemName: string;
  items: TSelectBase[];
  disabled?: boolean;
  onValueChange?: (value: string[]) => void;
  value?: string[];
  triggerClassName?: string;
  contentClassName?: string;
  portalContent?: boolean;
  portalElement?: HTMLElement;
}

function stringArrayEquality(a: string[], b: string[]) {
  return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((val, index) => val === b[index]);
}

const ComboboxMultiple = ({
  itemName,
  items,
  disabled = false,
  onValueChange,
  value,
  triggerClassName,
  contentClassName,
  portalContent = false,
  portalElement,
  ...props
}: Props) => {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState<boolean>(false);
  const [selected, setSelected] = useState<string[]>(value ?? []);
  const selectedItems = items.filter((item) => selected.includes(item.value));
  const listRef = useRef<HTMLDivElement | null>(null);
  const instanceId = useId();
  const getItemDomId = (val: string) => `combobox-${instanceId}-item-${encodeURIComponent(val)}`;
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    itemRefs.current = {};
  }, [items]);

  useEffect(() => {
    const isEqual = stringArrayEquality(value ?? [], selected);
    if (!isEqual) {
      setSelected(value ?? []);
    }
  }, [value]);

  useEffect(() => {
    const isEqual = stringArrayEquality(value ?? [], selected);
    if (onValueChange && !isEqual) {
      onValueChange(selected);
    }
  }, [selected]);

  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return;
    }
    if (wasOpenRef.current) return;
    wasOpenRef.current = true;

    const firstSelected = (value && value.length ? value[0] : selected[0]) ?? '';
    if (!firstSelected) return;
    const targetId = getItemDomId(firstSelected);
    const tryScroll = () => {
      const byRef = (itemRefs.current[firstSelected] as HTMLElement | null) ?? null;
      const byId = document.getElementById(targetId) as HTMLElement | null;
      const el = byRef || byId;
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        const container = listRef.current;
        if (container) {
          const cRect = container.getBoundingClientRect();
          const eRect = el.getBoundingClientRect();
          if (eRect.top < cRect.top) {
            container.scrollTop += eRect.top - cRect.top;
          } else if (eRect.bottom > cRect.bottom) {
            container.scrollTop += eRect.bottom - cRect.bottom;
          }
        }
        return true;
      }
      return false;
    };

    if (tryScroll()) return;
    requestAnimationFrame(() => {
      if (tryScroll()) return;
      setTimeout(() => {
        tryScroll();
      }, 120);
    });
  }, [open, selected, items, value]);

  return (
    <HubertPopover open={open} onOpenChange={setOpen} {...props}>
      <HubertPopover.Trigger asChild>
        <HubertButton
          variant="outline"
          role="combobox"
          size={'default'}
          aria-expanded={open}
          className={clsx(
            'justify-between',
            'min-w-[210px]',
            'text-ellipsis',
            'transition-all duration-200',
            'hover:bg-gray-50',
            'hover:text-primary',
            'hover:border-gray-300',
            'focus:ring-primary/20 focus:ring-2',
            'relative',
            'border-gray-200',
            'text-gray-700',
            'group',
            triggerClassName,
          )}
          icon={
            <ChevronDown
              className={cn(
                'h-4 w-4 text-gray-500 transition-all duration-200',
                'group-hover:text-primary',
                open && 'rotate-180',
              )}
            />
          }
          disabled={disabled}
        >
          <div className="flex items-center gap-1">
            {selected && selected.length ? (
              selected.length > 2 ? (
                <span className="flex items-center gap-1">
                  <span className="group-hover:text-primary truncate transition-colors duration-200">{itemName}</span>
                  <span
                    className={clsx(
                      'px-2 py-0.5',
                      'rounded-full',
                      'text-xs font-medium',
                      'bg-gray-50 text-gray-600',
                      'border border-gray-200',
                      'transition-colors duration-200',
                      'group-hover:bg-gray-100',
                      'group-hover:text-gray-700',
                      'group-hover:border-gray-300',
                    )}
                  >
                    {t('combobox.selectedCount', { count: selected.length })}
                  </span>
                </span>
              ) : (
                <div className="flex items-center gap-1">
                  {selectedItems.map((item) => (
                    <span
                      key={item.value}
                      className={clsx(
                        'flex items-center gap-1',
                        'px-2 py-0.5',
                        'rounded-full',
                        'text-xs font-medium',
                        'bg-gray-50 text-gray-600',
                        'border border-gray-200',
                        'transition-colors duration-200',
                        'group-hover:bg-gray-100',
                        'group-hover:text-gray-700',
                        'group-hover:border-gray-300',
                      )}
                    >
                      {item.icon ? <span className="flex-shrink-0">{item.icon}</span> : null}
                      <TruncatedLabel label={item.label} />
                    </span>
                  ))}
                </div>
              )
            ) : (
              <span
                className={clsx(
                  'truncate transition-colors duration-200',
                  'group-hover:bg-gray-100',
                  'group-hover:text-gray-700',
                  'group-hover:border-gray-300',
                )}
              >
                {t('combobox.selectItem', { itemName })}
              </span>
            )}
          </div>
        </HubertButton>
      </HubertPopover.Trigger>
      <HubertPopover.Content
        className={clsx(
          'z-10 w-[210px] p-0',
          'rounded-md border border-gray-200 shadow-xs',
          'bg-white',
          'animate-in fade-in-0 zoom-in-95',
          contentClassName,
        )}
        portalContent={portalContent}
        portalElement={portalElement}
        align="start"
      >
        <Command className="rounded-lg">
          <CommandInput
            placeholder={t('combobox.searchItem', { itemName })}
            className="h-9 border-b border-gray-100 text-gray-700 placeholder:text-gray-400"
          />

          <CommandList ref={listRef} className="max-h-[300px] overflow-y-auto">
            <CommandEmpty className="py-6 text-center text-sm text-gray-500">
              {t('combobox.noItemFound', { itemName })}
            </CommandEmpty>

            <CommandGroup className="p-1">
              {items.map((item) => {
                return (
                  <CommandItem
                    key={item.value}
                    id={getItemDomId(item.value)}
                    ref={(el) => {
                      itemRefs.current[item.value] = el as HTMLDivElement | null;
                    }}
                    disabled={disabled || item.disabled === true}
                    value={item.value}
                    onSelect={(currentValue) => {
                      if (item.disabled === true) {
                        return;
                      }
                      setSelected((prev) => toggleSelection(prev, currentValue));

                      if (item.onClick) {
                        item.onClick(currentValue);
                      }
                    }}
                    color={item.color}
                    keywords={[item.label]}
                    className={clsx(
                      'flex items-center gap-2',
                      'px-2 py-1.5',
                      'rounded-md',
                      'cursor-pointer',
                      'transition-colors duration-200',
                      'text-gray-700',
                      'hover:bg-gray-100',
                      'data-[selected=true]:bg-primary/10',
                      'data-[selected=true]:text-primary',
                      'data-[disabled=true]:opacity-50',
                      'data-[disabled=true]:cursor-not-allowed',
                      'data-[disabled=true]:bg-danger-50',
                      'data-[disabled=true]:text-danger-600',
                    )}
                  >
                    {item.icon ? <span className="flex-shrink-0">{item.icon}</span> : null}
                    <TruncatedLabel label={item.label} className="flex-1" />
                    <Check
                      className={cn(
                        'h-4 w-4',
                        'transition-opacity duration-200',
                        'text-primary',
                        selected.includes(item.value) ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </HubertPopover.Content>
    </HubertPopover>
  );
};

export default ComboboxMultiple;
